import { forwardRef } from 'react';
import { Download, Moon, RotateCcw, Search, Sun, X } from 'lucide-react';
import type { ScheduleModel } from '../../types/schedule';
import { formatDate } from './chartPrimitives';

export interface Filters {
  search: string;
  status: 'all' | 'Not Started' | 'In Progress' | 'Completed';
  criticality: 'all' | 'critical' | 'noncritical';
  wbsId: string;
  startDate: string;
  endDate: string;
  /** Activity-code filter as `${type}::${value}`, or 'all'. */
  activityCode: string;
  /** Explicit id set pinned by a drill-through from a chart or DCMA check. */
  pinnedIds: string[] | null;
  pinnedLabel: string | null;
}

export const EMPTY_FILTERS: Filters = {
  search: '',
  status: 'all',
  criticality: 'all',
  wbsId: 'all',
  startDate: '',
  endDate: '',
  activityCode: 'all',
  pinnedIds: null,
  pinnedLabel: null,
};

export function filtersAreActive(filters: Filters): boolean {
  return (
    filters.search !== '' ||
    filters.status !== 'all' ||
    filters.criticality !== 'all' ||
    filters.wbsId !== 'all' ||
    filters.startDate !== '' ||
    filters.endDate !== '' ||
    filters.activityCode !== 'all' ||
    filters.pinnedIds !== null
  );
}

interface CommandBarProps {
  schedule: ScheduleModel;
  baseline: ScheduleModel | null;
  filters: Filters;
  onFiltersChange: (next: Filters) => void;
  matchedCount: number;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  onExport: () => void;
  onReset: () => void;
}

/**
 * Sticky header holding project identity and the global filter set.
 *
 * Every filter here drives every panel in the workspace, not just the table —
 * a filter that silently applies to one view is worse than no filter.
 */
export const CommandBar = forwardRef<HTMLInputElement, CommandBarProps>(
  function CommandBar(
    {
      schedule,
      baseline,
      filters,
      onFiltersChange,
      matchedCount,
      theme,
      onToggleTheme,
      onExport,
      onReset,
    },
    searchRef
  ) {
    const update = (patch: Partial<Filters>) =>
      onFiltersChange({ ...filters, ...patch });

    const active = filtersAreActive(filters);

    // Only offer WBS nodes that actually hold activities.
    const wbsOptions = schedule.wbs
      .filter((node) => !node.isProjectNode)
      .slice(0, 300);

    const codeOptions = schedule.activityCodeTypes.flatMap((type) => {
      const values = new Set<string>();
      for (const activity of schedule.activities) {
        const value = activity.activityCodes[type];
        if (value) values.add(value);
      }
      return [...values].sort().map((value) => ({ type, value }));
    });

    return (
      <div
        className="sticky top-0 z-40 border-b backdrop-blur-md"
        style={{
          background: 'color-mix(in srgb, var(--surface-page) 88%, transparent)',
          borderColor: 'var(--border-subtle)',
        }}
      >
        <div className="mx-auto max-w-[1600px] px-4 py-3 lg:px-8">
          {/* Identity strip */}
          <div className="mb-3 flex flex-wrap items-baseline gap-x-5 gap-y-1">
            <h1
              className="text-base font-bold tracking-tight"
              style={{ color: 'var(--text-primary)' }}
            >
              {schedule.projectName}
            </h1>

            <span className="eyebrow" style={{ color: 'var(--text-muted)' }}>
              Data date
            </span>
            <span
              className="tnum text-xs font-semibold"
              style={{
                color: schedule.dataDateInferred
                  ? 'var(--status-warn)'
                  : 'var(--mr-vivid-blue)',
              }}
              title={
                schedule.dataDateInferred
                  ? 'Inferred from the latest actual date — not present in the file'
                  : 'From PROJECT.last_recalc_date'
              }
            >
              {formatDate(schedule.dataDate)}
              {schedule.dataDateInferred && ' (inferred)'}
            </span>

            <span className="tnum text-xs" style={{ color: 'var(--text-muted)' }}>
              {schedule.activities.length.toLocaleString('en-GB')} activities ·{' '}
              {schedule.relationships.length.toLocaleString('en-GB')} links
            </span>

            <span
              className={`chip ${baseline ? 'chip-pass' : 'chip-na'}`}
              title={
                baseline
                  ? `Baseline: ${baseline.fileName}`
                  : 'Upload a baseline to unlock variance, BEI and missed tasks'
              }
            >
              {baseline ? 'Baseline loaded' : 'No baseline'}
            </span>

            <div className="ml-auto flex items-center gap-2">
              <button
                className="icon-button"
                onClick={onToggleTheme}
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              >
                {theme === 'dark' ? (
                  <Sun className="h-3.5 w-3.5" />
                ) : (
                  <Moon className="h-3.5 w-3.5" />
                )}
              </button>
              <button className="icon-button" onClick={onExport} title="Export filtered activities as CSV">
                <Download className="h-3.5 w-3.5" />
                Export
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }}
              />
              <input
                ref={searchRef}
                type="text"
                className="control w-56 pl-7"
                placeholder="Search activities…  (press /)"
                value={filters.search}
                onChange={(e) => update({ search: e.target.value })}
              />
            </div>

            <select
              className="control"
              value={filters.status}
              onChange={(e) => update({ status: e.target.value as Filters['status'] })}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="Not Started">Not started</option>
              <option value="In Progress">In progress</option>
              <option value="Completed">Completed</option>
            </select>

            <select
              className="control"
              value={filters.criticality}
              onChange={(e) =>
                update({ criticality: e.target.value as Filters['criticality'] })
              }
              aria-label="Filter by criticality"
            >
              <option value="all">All activities</option>
              <option value="critical">Critical only</option>
              <option value="noncritical">Non-critical only</option>
            </select>

            {wbsOptions.length > 0 && (
              <select
                className="control max-w-[190px]"
                value={filters.wbsId}
                onChange={(e) => update({ wbsId: e.target.value })}
                aria-label="Filter by WBS"
              >
                <option value="all">All WBS</option>
                {wbsOptions.map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.code ? `${node.code} — ` : ''}
                    {node.name}
                  </option>
                ))}
              </select>
            )}

            {codeOptions.length > 0 && (
              <select
                className="control max-w-[190px]"
                value={filters.activityCode}
                onChange={(e) => update({ activityCode: e.target.value })}
                aria-label="Filter by activity code"
              >
                <option value="all">All codes</option>
                {codeOptions.map(({ type, value }) => (
                  <option key={`${type}::${value}`} value={`${type}::${value}`}>
                    {type}: {value}
                  </option>
                ))}
              </select>
            )}

            <div className="flex items-center gap-1">
              <input
                type="date"
                className="control w-[130px]"
                value={filters.startDate}
                onChange={(e) => update({ startDate: e.target.value })}
                aria-label="Finishing on or after"
                title="Finishing on or after"
              />
              <span style={{ color: 'var(--text-muted)' }}>–</span>
              <input
                type="date"
                className="control w-[130px]"
                value={filters.endDate}
                onChange={(e) => update({ endDate: e.target.value })}
                aria-label="Finishing on or before"
                title="Finishing on or before"
              />
            </div>

            <span
              className="tnum ml-auto text-xs"
              style={{ color: 'var(--text-secondary)' }}
            >
              {matchedCount.toLocaleString('en-GB')} of{' '}
              {schedule.activities.length.toLocaleString('en-GB')} shown
            </span>

            {active && (
              <button className="icon-button" onClick={onReset}>
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            )}
          </div>

          {/* Pinned drill-through */}
          {filters.pinnedIds && (
            <div className="mt-2 flex items-center gap-2">
              <span className="chip chip-info">
                {filters.pinnedLabel} · {filters.pinnedIds.length} activities
                <button
                  onClick={() => update({ pinnedIds: null, pinnedLabel: null })}
                  aria-label="Clear drill-through"
                  className="ml-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
);
